import { useEffect, useRef, useState } from 'react';
import { ParcelaPedidoCompletoType } from './types/PedidoTypes';
import { DadosListagemContasReceberCliente } from './types/ContaReceberTypes';
import { DUPR_STATUS, DUPR_STATUS_PAGO, MENSAGEM_RETORNO_POP_UP_BLOQUEADO } from './constants';
import dayjs from 'dayjs';
import MiniInfo from './Components/generic/MiniInfo';
import { colors } from './theme';
import { toast, ToastPosition } from 'react-toastify';
import { Theme, useMediaQuery, useTheme } from '@mui/material';

import { FileOpener } from '@capacitor-community/file-opener';
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

export const formatarDinheiro = (value: number | string) => {
  if (!value) {
    return '0,00';
  }

  if (typeof value === 'string') {
    value = parseFloat(value);
  }

  value = value.toFixed(2);
  value = value.replace('.', ',');
  if (value.length > 6) {
    value = value.replace(/([0-9]{3}),([0-9]{2}$)/g, '.$1,$2');
  }
  return value;
};

export const formatarData = (data: number | string | undefined) => {
  if (!data) {
    return '';
  }
  if (typeof data === 'number') {
    data = data.toString();
  }
  data = data.replace(/-/g, '');
  data = data.slice(6, 8) + '/' + data.slice(4, 6) + '/' + data.slice(0, 4);
  return data;
};

export const formatarHora = (hora: number | string) => {
  if (!hora) {
    return '0';
  }
  if (typeof hora === 'number') {
    hora = hora.toString();
  }
  hora = hora.slice(0, 2) + ':' + hora.slice(2, 4);
  return hora;
};

export const cpfMask = (value: number | string) => {
  if (!value) {
    return '';
  }
  if (typeof value !== 'string') {
    value = value.toString();
  }
  return value
    .replace(/\D/g, '') // substitui qualquer caracter que nao seja numero por nada
    .replace(/(\d{3})(\d)/, '$1.$2') // captura 2 grupos de numero o primeiro de 3 e o segundo de 1, apos capturar o primeiro grupo ele adiciona um ponto antes do segundo grupo de numero
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1'); // captura 2 numeros seguidos de um traço e não deixa ser digitado mais nada
};

export const cnpjMask = (value: number | string) => {
  if (!value) {
    return '';
  }
  if (typeof value !== 'string') {
    value = value.toString();
  }
  return value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1');
};

export const hexWithAlpha = (hexCode: string, opacity: number) => {
  let hex = hexCode.replace('#', '');

  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r},${g},${b},${opacity})`;
};

export function mudarIluminacao(hex: string, lum: number) {
  // lum > 0 == mais claro
  // lum < 0 == mais escuro

  // validate hex string
  hex = String(hex).replace(/[^0-9a-f]/gi, '');
  if (hex.length < 3) {
    return '#000';
  }
  if (hex.length < 6) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  lum = lum || 0;

  // convert to decimal and change luminosity
  let rgb = '#',
    c,
    i;
  for (i = 0; i < 3; i++) {
    c = parseInt(hex.substr(i * 2, 2), 16);
    c = Math.round(Math.min(Math.max(0, c + c * lum), 255)).toString(16);
    rgb += ('00' + c).substr(c.length);
  }

  return rgb;
}

export function useWindowSize() {
  // Initialize state with undefined width/height so server and client renders match

  // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/

  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      // Set window width/height to state
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    }

    // Add event listener
    window.addEventListener('resize', handleResize);
    // Call handler right away so state gets updated with initial window size
    handleResize();
    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty array ensures that effect is only run on mount

  return windowSize;
}
// Hook
export function useLocalStorage<T>(key: string, initialValue: T): [T, (obj: T) => void] {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(key);
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue;
    } catch (error: any) {
      // If error also return initialValue
      console.log(error);
      return initialValue;
    }
  });
  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value: T) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to local storage
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error: any) {
      // A more advanced implementation would handle the error case
      console.log(error);
    }
  };

  return [storedValue, setValue];
}

export function usePageStorage<T>(
  page: string,
  key: string,
  initialValue: T
): [T, (obj: T) => void] {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      // Get from local storage by key
      const pageStr = window.localStorage.getItem(page);
      const pageParsed = pageStr ? JSON.parse(pageStr) : {};

      const item = pageParsed[key];

      return item ? item : initialValue;
    } catch (error: any) {
      // If error also return initialValue
      console.log(error);
      return initialValue;
    }
  });
  const setValue = (value: T) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);

      // Save to local storage
      const pageStr = window.localStorage.getItem(page);
      const pageParsed = pageStr ? JSON.parse(pageStr) : {};
      pageParsed[key] = valueToStore;
      window.localStorage.setItem(page, JSON.stringify(pageParsed));
    } catch (error: any) {
      // A more advanced implementation would handle the error case
      console.log(error);
    }
  };

  return [storedValue, setValue];
}

export function getItemPageStorage<T>(page: string, key: string): T {
  const pageStr = window.localStorage.getItem(page);
  const pageParsed = pageStr ? JSON.parse(pageStr) : {};
  const item = pageParsed[key];
  return item;
}

/**
 * Funciona da mesma forma que o useEffect, mas não
 * é chamado na renderização inicial, apenas nos updates.
 *
 * @date 2020-12-31
 * @param {any} effect:React.EffectCallback
 * @param {any} deps?:React.DependencyList|undefined
 * @returns {any}
 */
export const useUpdateEffect = (
  effect: React.EffectCallback,
  deps?: React.DependencyList | undefined
) => {
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      effect();
    }
  }, deps);
};

export function parseData(data: string | number) {
  if (!data) {
    return '';
  }
  data = data.toString().replace(/\//g, '');
  data = data.padStart(8, '0');
  return data.slice(6, 8) + '/' + data.slice(4, 6) + '/' + data.slice(0, 4);
}

export function parseIntMelhorado(n: string) {
  if (!n) {
    return 0;
  }
  const value = parseInt(n.replace(/\D/g, ''));
  return isNaN(value) ? 0 : value;
}

export const blobToBase64 = (blob: Blob) => {
  const reader = new FileReader();
  reader.readAsDataURL(blob);
  return new Promise<string>((resolve, reject) => {
    reader.onloadend = () => {
      resolve(reader.result as string);
    };
    reader.onerror = (err) => {
      reject(err);
    };
  });
};

export function desinverteData(data: string | number) {
  if (!data) {
    return '';
  }
  data = data.toString().replace(/\//g, '');
  data = data.padStart(8, '0');
  return data.slice(6, 8) + data.slice(4, 6) + data.slice(0, 4);
}

export function inverteData(data: string | number) {
  if (!data) {
    return '';
  }
  data = data.toString().replace(/\//g, '');
  data = data.padStart(8, '0');
  return data.slice(4, 8) + data.slice(2, 4) + data.slice(0, 2);
}

export const getTipoStatus = (
  conta: DadosListagemContasReceberCliente | ParcelaPedidoCompletoType
) => {
  const status = DUPR_STATUS.find((s) => s.codigo === conta.status);
  const hoje = dayjs().format('YYYYMMDD');
  const vencido =
    status && status.codigo !== DUPR_STATUS_PAGO && parseInt(conta.vencimento) < parseInt(hoje);
  const aguardandoConciliacao =
    (conta as DadosListagemContasReceberCliente).aguardandoConciliacao === true;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        justifyContent: 'center',
        padding: '5px',
        textAlign: 'center'
      }}
    >
      {aguardandoConciliacao ? (
        <MiniInfo style={{ width: '100%' }} color={colors.blue}>
          Pagamento identificado
        </MiniInfo>
      ) : (
        <>
          {status && !vencido && (
            <MiniInfo style={{ width: '100%' }} color={status.cor}>
              {status.descricao}
              {Boolean(conta.dataPagamento) && (
                <>
                  <br />
                  {parseData(conta.dataPagamento)}
                </>
              )}
            </MiniInfo>
          )}

          {vencido && (
            <MiniInfo style={{ width: '100%' }} color={colors.red}>
              VENCIDO
            </MiniInfo>
          )}
        </>
      )}

      {aguardandoConciliacao && (
        <MiniInfo style={{ width: '100%', marginTop: 4 }} color={colors.darkGrey}>
          Aguardando conciliação.
        </MiniInfo>
      )}
    </div>
  );
};

export const mostrarToast = (
  msg: string,
  severity: 'error' | 'success' | 'warning',
  position?: ToastPosition
) => {
  const options = {
    position: position ?? 'top-right'
  };

  if (severity === 'error') {
    toast.error(msg, options);
  } else if (severity === 'success') {
    toast.success(msg, options);
  } else if (severity === 'warning') {
    toast.warning(msg, options);
  }
};

export function isMobile() {
  return window.matchMedia('(max-width: 768px)').matches;
}

export function copyToClipboard(text: string) {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).catch((err) => {
      console.error('Erro ao copiar texto:', err);
    });
  } else {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand('copy');
    } catch (err) {
      console.error('Erro ao copiar texto:', err);
    }
    document.body.removeChild(textarea);
  }
}

export type SupportedContentTypes =
  | 'application/pdf'
  | 'application/zip'
  | 'application/xml'
  | 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  | 'text/csv';

export const createFileFromBlob = async (
  blob: Blob,
  fileName: string,
  contentType: SupportedContentTypes
) => {
  // Check if we are in a web environment
  if (!Capacitor.isNativePlatform()) {
    // Web: Use standard download via <a> element
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;

    // Append the link to the document and trigger the download
    document.body.appendChild(link);
    link.click();

    // Clean up and remove the link
    document.body.removeChild(link);
  } else {
    try {
      const base64Data = await blobToBase64(blob);

      // Salva no diretório Cache (não requer permissões especiais)
      const result = await Filesystem.writeFile({
        path: fileName,
        data: base64Data,
        directory: Directory.Cache,
        recursive: true
      });

      try {
        // Tenta abrir com aplicativo padrão
        await FileOpener.open({
          filePath: result.uri,
          contentType
        });
      } catch (openError) {
        // Se não conseguir compartilha o arquivo
        await Share.share({
          title: fileName,
          url: result.uri,
          dialogTitle: 'Compartilhar arquivo'
        });
      }
    } catch (error) {
      console.error('Erro no processo:', error);
    }
  }
};

export async function openFileFromBlob(
  blob: Blob,
  fileName: string,
  contentType: SupportedContentTypes
) {
  if (Capacitor.isNativePlatform()) {
    await createFileFromBlob(blob, fileName, contentType);
  } else {
    const url = URL.createObjectURL(blob);
    const newWindow = window.open(url, '_blank');

    if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
      throw new Error(MENSAGEM_RETORNO_POP_UP_BLOQUEADO);
    }
  }
}

/**
 * Valida CPF, aceitando tanto apenas dígitos quanto strings formatadas com máscara.
 */
export function validarCPF(strCPF: string): boolean {
  if (!strCPF) {
    return false;
  }

  strCPF = strCPF.replace(/\D/g, '');

  if (strCPF.length !== 11) {
    return false;
  }

  let soma: number;
  let resto: number;
  soma = 0;

  if (strCPF === '00000000000') return false;

  for (let i = 1; i <= 9; i++) {
    soma += parseInt(strCPF.substring(i - 1, i)) * (11 - i);
  }

  resto = (soma * 10) % 11;

  if (resto === 10 || resto === 11) {
    resto = 0;
  }
  if (resto !== parseInt(strCPF.substring(9, 10))) {
    return false;
  }

  soma = 0;
  for (let i = 1; i <= 10; i++) {
    soma += parseInt(strCPF.substring(i - 1, i)) * (12 - i);
  }
  resto = (soma * 10) % 11;

  if (resto === 10 || resto === 11) {
    resto = 0;
  }
  if (resto !== parseInt(strCPF.substring(10, 11))) {
    return false;
  }
  return true;
}

/**
 * Valida CNPJ, aceitando tanto apenas dígitos quanto strings formatadas com máscara.
 */
export function validarCNPJ(cnpj: string): boolean {
  if (!cnpj) {
    return false;
  }

  cnpj = cnpj.replace(/\D/g, '');

  if (cnpj.length !== 14) {
    return false;
  }

  // Elimina CNPJs inválidos conhecidos
  if (
    cnpj === '00000000000000' ||
    cnpj === '11111111111111' ||
    cnpj === '22222222222222' ||
    cnpj === '33333333333333' ||
    cnpj === '44444444444444' ||
    cnpj === '55555555555555' ||
    cnpj === '66666666666666' ||
    cnpj === '77777777777777' ||
    cnpj === '88888888888888' ||
    cnpj === '99999999999999'
  ) {
    return false;
  }

  // Valida DVs
  let tamanho = cnpj.length - 2;
  let numeros = cnpj.substring(0, tamanho);
  const digitos = cnpj.substring(tamanho);
  let soma = 0;
  let pos = tamanho - 7;
  for (let i = tamanho; i >= 1; i--) {
    soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
  if (resultado !== parseInt(digitos.charAt(0))) return false;

  tamanho = tamanho + 1;
  numeros = cnpj.substring(0, tamanho);
  soma = 0;
  pos = tamanho - 7;
  for (let i = tamanho; i >= 1; i--) {
    soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
  if (resultado !== parseInt(digitos.charAt(1))) return false;

  return true;
}
export function validarDocumento(doc: string): boolean {
  const digitos = doc.replace(/\D/g, '');
  if (digitos.length === 11) {
    return validarCPF(digitos);
  }
  if (digitos.length === 14) {
    return validarCNPJ(digitos);
  }
  return false;
}

/**
 * Valida a política de senha do portal (mínimo 6 caracteres, com letras e números).
 * Espelha `validarDadosSenha` do backend — mantenha as regras em sincronia.
 * Delega para `getRequisitosSenha`, que é a fonte única dos requisitos exibidos na UI.
 */
export function getErroSenha(senha: string): string | null {
  const naoAtendido = getRequisitosSenha(senha).find((requisito) => !requisito.atendido);
  if (!naoAtendido) return null;
  return naoAtendido.id === 'minimo'
    ? 'A senha deve ter pelo menos 6 caracteres.'
    : 'A senha deve conter letras e números.';
}

export function senhaAtendePolitica(senha: string): boolean {
  return getErroSenha(senha) === null;
}

export function mascararEmail(email: string) {
  const partes = email.split('@');
  const usuarioMascarado =
    partes[0].slice(0, Math.floor(partes[0].length / 2)) +
    '*'.repeat(partes[0].length - Math.floor(partes[0].length / 2));
  return usuarioMascarado + '@' + partes[1];
}

export function mascararTelefone(telefone: string) {
  return '*****' + telefone.substring(telefone.length, telefone.length - 4);
}

export const formatarFatura = (fatura: string) => {
  if (!fatura || fatura.length === 0) return '';
  const stringFatura = fatura?.padStart(10, '0');
  return `${stringFatura?.substring(0, 6)}/${stringFatura?.substring(
    6,
    8
  )}-${stringFatura?.substring(8, 10)}`;
};

// Wrapper criado para facilitar a migração MUIv5 //
type StyleObj = Record<string, object>;
type StyleFn<T extends StyleObj> = (theme: Theme) => T;

export function legacyMakeStyles<T extends StyleObj>(styles: StyleFn<T> | StyleObj) {
  return () => {
    if (typeof styles === 'function') {
      const theme = useTheme();
      return styles(theme);
    } else {
      return styles;
    }
  };
}

// Use fora de componentes React (ex.: grupos, rotas, configs), onde Hooks não estão disponíveis. Retorna o valor no momento da chamada, sem reatividade.
export const getIsSmallScreen = () => {
  return window.innerWidth <= 600;
};

// Use dentro de componentes React ou Hooks customizados. Reage automaticamente às mudanças de tamanho da tela via useMediaQuery.
export const useIsSmallScreen = () => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('sm'));
};

export const TAMANHO_MINIMO_SENHA = 6;

export type RequisitoSenha = {
  id: 'minimo' | 'letrasNumeros';
  label: string;
  atendido: boolean;
};

export function getRequisitosSenha(senha: string): RequisitoSenha[] {
  const valor = senha ?? '';
  return [
    {
      id: 'minimo',
      label: `Pelo menos ${TAMANHO_MINIMO_SENHA} caracteres`,
      atendido: valor.length >= TAMANHO_MINIMO_SENHA
    },
    {
      id: 'letrasNumeros',
      label: 'Conter letras e números',
      atendido: /[a-zA-Z]/.test(valor) && /[0-9]/.test(valor)
    }
  ];
}
